import React from 'react'
import { useTranslation } from 'next-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import { AvatarUploader } from 'shared/components/avatar-uploader'
import FormInput from 'shared/components/form-input'
import { FormTextArea } from 'shared/components/form-textarea'
import Popover from 'shared/components/popover'
import { FILE_SOURCE } from 'shared/constants/file-source-constants'
import QuestionMarkIcon from 'shared/icons/question-mark-icon'
import {
  UserDomainAccountInterface,
  UserDomainAccountSettingsErrorsInterface,
} from '../types/user-settings-interface'

interface UserDomainAccountFieldsProps {
  setProfileImage: (dataFile: UserDomainAccountInterface['profileImage']) => void
  setDisplayName: (displayName: UserDomainAccountInterface['displayName']) => void
  setDisplayBio: (bio: UserDomainAccountInterface['bio']) => void
  displayName: UserDomainAccountInterface['displayName']
  bio: UserDomainAccountInterface['bio']
  profileImage: string
  email: string
  errors: UserDomainAccountSettingsErrorsInterface
}

function UserDomainAccountFields({
  displayName,
  bio,
  setDisplayName,
  setDisplayBio,
  setProfileImage,
  profileImage,
  email,
  errors,
}: UserDomainAccountFieldsProps) {
  const { t } = useTranslation()

  return (
    <>
      <div className="flex flex-col justify-between gap-0 md:flex-row md:gap-10 [&>*]:flex-1">
        <div className={'flex basis-full flex-col items-start gap-1'}>
          <div className="flex gap-1.5 font-avertaPE font-normal text-text-secondary">
            {t('user.profile_settings.profile_image')}
            <Popover
              label={t('user.profile_settings.profile_image_hint')}
              popoverClassName={'w-[200px] sm:w-[350px]'}
            >
              {show => (
                <QuestionMarkIcon
                  className={twJoin('size-4', show ? 'fill-brand-primary' : 'fill-text-secondary')}
                />
              )}
            </Popover>
          </div>
          <AvatarUploader
            type="profile_image"
            source={FILE_SOURCE.settings}
            onSetPhoto={setProfileImage}
          >
            <img
              className={'rounded-full border-2 border-brand-primary object-cover'}
              alt={email}
              width={128}
              height={128}
              src={profileImage}
            />
          </AvatarUploader>
        </div>
      </div>
      <div className="flex flex-col justify-between gap-0 md:flex-row md:gap-10 [&>*]:flex-1">
        <FormInput
          placeholder={t('user.profile_settings.first_name.label')}
          label={
            <div className="flex gap-1.5 font-avertaPE text-base font-normal text-text-secondary">
              {t('user.profile_settings.display_name.label')}
              <Popover
                label={t('user.profile_settings.display_name.help_message')}
                popoverClassName={'w-[200px] sm:w-[350px]'}
              >
                {show => (
                  <QuestionMarkIcon
                    className={twJoin(
                      'size-4',
                      show ? 'fill-brand-primary' : 'fill-text-secondary',
                    )}
                  />
                )}
              </Popover>
            </div>
          }
          required
          value={displayName || ''}
          onChange={e => setDisplayName(e.target.value)}
          error={errors.displayName}
        />
        <div />
      </div>
      <div className="flex flex-col justify-between gap-0 md:flex-row md:gap-10 [&>*]:flex-1">
        <FormTextArea
          rows={5}
          placeholder={t('user.profile_settings.display_bio.input_placeholder')}
          label={
            <div className="flex gap-1.5 font-avertaPE text-base font-normal text-text-secondary">
              {t('user.profile_settings.display_bio.label')}
              <Popover
                label={t('user.profile_settings.display_bio.help_message')}
                popoverClassName={'w-[200px] sm:w-[350px]'}
              >
                {show => (
                  <QuestionMarkIcon
                    className={twJoin(
                      'size-4',
                      show ? 'fill-brand-primary' : 'fill-text-secondary',
                    )}
                  />
                )}
              </Popover>
            </div>
          }
          maxLength={255}
          value={bio || ''}
          onChange={e => setDisplayBio(e.target.value)}
          error={errors.bio}
        />
        <div />
      </div>
    </>
  )
}

export default UserDomainAccountFields
